import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { isBundleCartItem, skusForCartItem } from '../lib/cart';
import { getProductsBySku } from '../lib/product';
import { cartActions as storeCartActions } from './storeCart';
import * as types from '../types';
import { COLLAR_TARIFF_FEE_SKU } from '../views/ProductDetails/Series3CollarDetails/Series3CollarDetails';

const initialState: types.ECommerceConfig = {
  loaded: false,
  upgradeFlow: false,
  products: [],
  shippingOptions: [],
  siteConfig: {
    // These will be fetched from the remote config, but we prefer them required in the type definition
    // so we'll set some defaults from the current remote config values
    gwpRenewalMonths: 6,
    gwpFreeTrialDisplayValueInCents: 7900,
    gwpFreeTrialMonths: 3,
  },
};

const name = 'config' as const;

const { actions, reducer } = createSlice({
  name,
  initialState,
  reducers: {
    set(_state: types.ECommerceConfig, { payload }: PayloadAction<types.ECommerceConfig>) {
      return {
        ...payload,
      };
    },
    clearConfig() {
      return initialState;
    },
  },
});

export const configActions = {
  ...actions,

  setConfig:
    (remoteConfig: types.RemoteConfig, upgradeFlow: boolean) =>
    (dispatch: Dispatch, getState: () => types.AppState) => {
      dispatch(
        actions.set({
          loaded: true,
          products: remoteConfig.products,
          shippingOptions: remoteConfig.shippingOptions,
          siteConfig: remoteConfig.config,
          upgradeFlow,
        }),
      );

      // Remove any items from the cart that are no longer part of the product config
      // (I.e. if a chip reseller logs out, remove any Fi nano boxes.)
      // TODO: Do this for carts of all shops
      const cart = getState().storeShop.cart;
      const productsBySku = getProductsBySku(remoteConfig.products);

      let cartItemIdsToRemove = Object.values(cart.cartItems)
        .filter((cartItem) =>
          skusForCartItem(cartItem).some((sku) => sku !== COLLAR_TARIFF_FEE_SKU && !productsBySku.get(sku)),
        )
        .map((li) => li.cartItemId);

      // Remove collar kits that do not have a tariff fee
      const collarTariffFeeActive = remoteConfig.config.collarTariffFeeActive;
      cartItemIdsToRemove = cartItemIdsToRemove.concat(
        Object.values(cart.cartItems)
          .filter((cartItem) => {
            if (!isBundleCartItem(cartItem)) {
              return false;
            }
            const product = productsBySku.get(cartItem.subscriptionLineItem.sku);
            if (
              !product ||
              !('canBePurchasedForProductIds' in product) ||
              !product.canBePurchasedForProductIds?.includes(types.series3CollarId)
            ) {
              return false;
            }
            const collarHasTariffFee = cartItem.addonLineItems?.some(
              (lineItem) => lineItem.sku === COLLAR_TARIFF_FEE_SKU,
            );
            if (collarHasTariffFee) {
              return !collarTariffFeeActive;
            } else {
              return collarTariffFeeActive;
            }
          })
          .map((li) => li.cartItemId),
      );

      // Remove cart items where the collar kit is an S3+ but there's an S3 subscription bundled with it. This happened
      // when there was a bug on the GWP redemption page.
      cartItemIdsToRemove = cartItemIdsToRemove.concat(
        Object.values(cart.cartItems)
          .filter((cartItem) => {
            if (!isBundleCartItem(cartItem)) {
              return false;
            }

            const subscriptionProduct = productsBySku.get(cartItem.subscriptionLineItem.sku);
            const lineItemProduct = productsBySku.get(cartItem.lineItem.sku);

            // There was a bug that added an S3+ collar kit to the cart with an S3 plan, let's try to detect that
            // and remove the cart item if there's a mismatch
            if (
              lineItemProduct?.id === types.series3PlusCollarId &&
              subscriptionProduct &&
              types.isModuleSubscriptionProduct(subscriptionProduct) &&
              !subscriptionProduct.canBePurchasedForProductIds.includes(types.series3PlusCollarId)
            ) {
              return true;
            }

            return false;
          })
          .map((li) => li.cartItemId),
      );

      cartItemIdsToRemove.forEach((cartItemId) => dispatch(storeCartActions.removeCartItem({ id: cartItemId })));
    },
};

export const configReducer = reducer;
export const configInitialState = { [name]: initialState };
